import { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { sessionChecker } from '../../lib/utils/sessionChecker';
import { OAuthCallbackLayout } from '../../components/oauth/OAuthCallbackLayout';
import { SsoTwoFactorStep } from '../../components/auth/SsoTwoFactorStep';
import { clear2faPendingFlag, set2faPendingFlag, setTwoFactorSession } from '../../lib/hooks/useTwoFactor';
import { tryTrustedDeviceLogin } from '../../lib/twofa/twofaApi';
import { TERMS_ACCEPTANCE_STORAGE_KEY } from '../../lib/config/termsVersion';
import { acceptInvite, InviteApiError, INVITE_ACCEPTANCE_STORAGE_KEY, type InviteOAuthAcceptancePayload } from '../../lib/services/invitesApi';
import { collectAttributionData, ensureEventId } from '../../lib/attribution/attributionCookie';
import { takeOAuthReturnPath } from '../../lib/utils/oauthReturnPath';

/**
 * Handles the redirect callback from OAuth providers (Google, Apple, Microsoft).
 * Supabase automatically exchanges the auth code for a session.
 * This page checks if the user has an organization and redirects accordingly.
 */
interface ProceedContext {
  profile: { organization_id: string; is_super_admin?: boolean; is_partner_admin?: boolean };
  userId: string;
  provider: string;
}

export function OAuthCallback() {
  const [error, setError] = useState<string | null>(null);
  // Step 2FA post-SSO (decisione 16 lug 2026: l'SSO NON salta il 2FA del CRM)
  const [twoFaStep, setTwoFaStep] = useState<{ email: string; methods: ('totp' | 'email')[] } | null>(null);
  const proceedCtxRef = useRef<ProceedContext | null>(null);
  const navigate = useNavigate();
  const processedRef = useRef(false);
  // Pagina richiesta prima del login, salvata da signInWithOAuth / One Tap (solo percorsi interni)
  const returnPathRef = useRef<string | null>(null);

  // Coda finale del login (tracking provider, dual-role, session checker,
  // default route): eseguita subito se il 2FA non serve, oppure DOPO la
  // verifica del codice nello step SsoTwoFactorStep.
  const proceedToApp = useCallback(async ({ profile, userId, provider }: ProceedContext) => {
    supabase
      .from('user_profiles')
      .update({ last_auth_provider: provider })
      .eq('id', userId)
      .then(({ error: updateError }) => {
        if (updateError) {
          console.error('[OAuthCallback] Error updating last_auth_provider:', updateError);
        }
      });

    if (profile.is_super_admin || profile.is_partner_admin) {
      console.log('[OAuthCallback] Dual-role user detected, defaulting to CRM mode');
      localStorage.setItem('flusia_active_role', 'crm');
    }

    try {
      await sessionChecker.initializeAfterLogin(userId);
    } catch (err) {
      console.error('[OAuthCallback] Error initializing session checker:', err);
    }

    // Stessa priorita' del login con password: pagina richiesta > default_route > '/'
    if (returnPathRef.current) {
      console.log('[OAuthCallback] Redirecting to original URL:', returnPathRef.current);
      navigate(returnPathRef.current, { replace: true });
      return;
    }

    try {
      const { data: settingsData } = await supabase
        .from('settings')
        .select('default_route')
        .eq('organization_id', profile.organization_id)
        .single();

      if (settingsData?.default_route) {
        console.log('[OAuthCallback] Redirecting to default route:', settingsData.default_route);
        navigate(settingsData.default_route, { replace: true });
        return;
      }
    } catch (err) {
      console.error('[OAuthCallback] Error getting default route:', err);
    }

    navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const handleCallback = async () => {
      try {
        // Errore restituito direttamente dal provider OAuth (es. consenso rifiutato)
        const params = new URLSearchParams(window.location.hash.replace(/^#/, '') || window.location.search);
        const providerError = params.get('error_description') || params.get('error');

        // Ritorno da un linkIdentity() (utente già loggato che collega Google/Microsoft dalle
        // Impostazioni, non un login/signup) — torna dove era partito invece della logica di login.
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get('intent') === 'link') {
          // Defense-in-depth: returnTo solo path interno (un singolo '/' iniziale).
          // react-router blocca già i redirect cross-origin, ma qui evitiamo anche
          // route interne arbitrarie con query param attacker-controlled.
          const rawReturnTo = searchParams.get('returnTo') || '';
          const returnTo = /^\/(?![\/\\])/.test(rawReturnTo) ? rawReturnTo : '/settings?tab=security';
          const separator = returnTo.includes('?') ? '&' : '?';
          // Provider passato dal componente di linking; default azure per retrocompatibilità.
          // Esiti (successo E errore) scoped per provider: in Impostazioni → Sicurezza montano
          // entrambe le card di linking e un param generico verrebbe consumato dalla prima.
          const linkProvider = searchParams.get('provider') === 'google' ? 'google' : 'microsoft';
          const providerLabel = linkProvider === 'google' ? 'Google' : 'Microsoft';
          if (providerError) {
            navigate(`${returnTo}${separator}${linkProvider}_link_error=${encodeURIComponent(providerError)}`, { replace: true });
            return;
          }
          const { error: sessionError } = await supabase.auth.getSession();
          if (sessionError) {
            navigate(`${returnTo}${separator}${linkProvider}_link_error=${encodeURIComponent(`Errore durante il collegamento con ${providerLabel}.`)}`, { replace: true });
            return;
          }
          navigate(`${returnTo}${separator}${linkProvider}_linked=1`, { replace: true });
          return;
        }

        // Letta una volta sola (e tolta) anche se l'accesso poi non riesce
        returnPathRef.current = takeOAuthReturnPath();

        if (providerError) {
          console.error('[OAuthCallback] Provider returned an error:', providerError);
          navigate(`/login?sso_error=${encodeURIComponent(providerError)}`, { replace: true });
          return;
        }

        // Il client Supabase ha detectSessionInUrl: false (fix impersonate in
        // supabase.ts): i token del flusso implicit tornano nel fragment ma
        // NESSUNO li processa automaticamente. Qui — e solo qui — materializziamo
        // la sessione a mano, poi puliamo i token dall'URL/history.
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        if (accessToken && refreshToken) {
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (setSessionError) {
            console.error('[OAuthCallback] setSession from URL hash failed:', setSessionError);
          }
          window.history.replaceState({}, '', window.location.pathname + window.location.search);
        }

        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          console.error('[OAuthCallback] No session after OAuth redirect:', sessionError);
          setError('Errore durante l\'autenticazione. Riprova.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Login SSO riuscito: pulisce un eventuale flag 2FA pending lasciato da un
        // tentativo password interrotto (altrimenti auth.ts fa signOut al reload).
        // SOLO se la sessione corrente è stata DAVVERO creata via OAuth/SSO (claim amr
        // del JWT): /auth/callback è una route pubblica anche in prod e una visita
        // manuale con una sessione password non ancora verificata 2FA NON deve
        // pulire il flag (= bypass 2FA). Fail-closed: se il JWT non è decodificabile
        // il flag resta e il check 2FA di auth.ts continua a proteggere.
        try {
          const payloadB64 = session.access_token.split('.')[1] || '';
          const payloadJson = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
          const amr: Array<{ method: string; timestamp?: number }> =
            JSON.parse(payloadJson)?.amr || [];
          const latest = [...amr].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0];
          if (latest && (latest.method === 'oauth' || latest.method === 'sso')) {
            clear2faPendingFlag();
          }
        } catch {
          // JWT non decodificabile: non pulire (fail-closed)
        }

        const userId = session.user.id;
        const provider = session.user.app_metadata?.provider || 'unknown';
        console.log('[OAuthCallback] OAuth session established, provider:', provider);

        // Check if user has an organization
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('organization_id, active, is_super_admin, is_partner_admin')
          .eq('id', userId)
          .single();

        if (profileError) {
          console.error('[OAuthCallback] Error fetching profile:', profileError);
          setError('Errore nel caricamento del profilo. Riprova.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Check if user is deactivated
        if (profile?.active === false) {
          console.warn('[OAuthCallback] User account is deactivated');
          await supabase.auth.signOut({ scope: 'local' });
          setError('Il tuo account è stato disattivato. Contatta l\'amministratore.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Ramo invito: l'utente ha avviato l'accettazione di un invito team via
        // social (bottone Google/Microsoft su /invite/:token, vedi InviteAcceptForm).
        // Va valutato PRIMA del ramo "nessuna organizzazione" qui sotto: un utente
        // invitato via OAuth arriva qui con una sessione appena creata ma
        // organization_id ancora NULL (skeleton profile creato dal trigger
        // handle_new_user su auth.users) — è acceptInvite (via /accept-oauth) ad
        // assegnare organizzazione+ruolo lato server.
        let pendingInviteToken: string | null = null;
        try {
          const storedInvite = sessionStorage.getItem(INVITE_ACCEPTANCE_STORAGE_KEY);
          if (storedInvite) {
            const invitePayload = JSON.parse(storedInvite) as InviteOAuthAcceptancePayload;
            pendingInviteToken = invitePayload.token;
            await acceptInvite(
              {
                token: invitePayload.token,
                firstName: invitePayload.firstName,
                lastName: invitePayload.lastName,
                terms: invitePayload.terms,
              },
              session.access_token,
            );
            sessionStorage.removeItem(INVITE_ACCEPTANCE_STORAGE_KEY);
            // Full reload (non navigate SPA): AuthProvider/permissionsStore erano stati
            // bootstrappati con organization_id=null PRIMA di acceptInvite; serve un
            // re-bootstrap completo per caricare l'org appena assegnata, altrimenti
            // RootRedirect vede permessi vuoti e rimbalza su /login. Atterra sul
            // mini-onboarding invitato (foto profilo + moduli), poi dashboard.
            window.location.replace('/welcome/profile');
            return;
          }
        } catch (inviteErr) {
          sessionStorage.removeItem(INVITE_ACCEPTANCE_STORAGE_KEY);
          console.error('[OAuthCallback] Invite acceptance failed:', inviteErr);
          if (pendingInviteToken) {
            await supabase.auth.signOut({ scope: 'local' });
            let errorCode = 'accept_failed';
            if (inviteErr instanceof InviteApiError) {
              if (inviteErr.code === 'email_mismatch') errorCode = 'email_mismatch';
              else if (inviteErr.code === 'already_registered') errorCode = 'already_registered';
            }
            navigate(`/invite/${pendingInviteToken}?error=${errorCode}`, { replace: true });
            return;
          }
          // JSON malformato senza token noto: nessun posto dove tornare, si prosegue
          // col flusso normale sotto (fail-open, non blocca un login OAuth legittimo).
        }

        // Registrazione completa = almeno un ruolo in user_roles (stesso
        // discriminatore di RegisterPage): organization_id NON basta, perché
        // register-oauth-start crea l'org placeholder già al primo giro — un
        // utente che torna indietro nel wizard e riclicca il social rientrerebbe
        // qui come "registrato" e finirebbe su una dashboard senza permessi
        // (schermata bianca). Super/partner admin non hanno righe user_roles:
        // mai mandarli nel wizard. Su errore del check manteniamo il
        // comportamento storico (si prosegue come registrato).
        let registrationPending = !profile?.organization_id;
        if (!registrationPending && !profile?.is_super_admin && !profile?.is_partner_admin) {
          const { count: rolesCount, error: rolesError } = await supabase
            .from('user_roles')
            .select('user_id', { count: 'exact', head: true })
            .eq('user_id', userId);
          registrationPending = !rolesError && (rolesCount ?? 0) === 0;
        }

        if (registrationPending) {
          console.log('[OAuthCallback] Registration incomplete, starting registration wizard');
          // Accettazione T&C salvata da RegisterPage prima del redirect OAuth:
          // la inoltriamo alla edge per il log a DB (best-effort, può mancare
          // se l'utente è arrivato al provider da un percorso diverso).
          let termsAcceptance: { version: string; vexatiousAccepted: boolean; acceptedAt: string } | null = null;
          try {
            const stored = sessionStorage.getItem(TERMS_ACCEPTANCE_STORAGE_KEY);
            if (stored) {
              termsAcceptance = JSON.parse(stored);
              sessionStorage.removeItem(TERMS_ACCEPTANCE_STORAGE_KEY);
            }
          } catch (e) {
            console.warn('[OAuthCallback] Unable to read terms acceptance:', e);
          }
          const attributionData = ensureEventId(collectAttributionData());
          const { data: startData, error: startError } = await supabase.functions.invoke('register-oauth-start', {
            body: termsAcceptance ? { termsAcceptance, attributionData } : { attributionData }
          });

          if (startError || !startData?.sessionToken) {
            console.error('[OAuthCallback] Error starting OAuth registration:', startError, startData);
            setError(startData?.error || 'Errore nell\'avvio della registrazione. Riprova.');
            setTimeout(() => navigate('/login'), 3000);
            return;
          }

          // Nuovo flusso: il ramo OAuth salta verify+password e atterra sullo step
          // Profilo (nome/cognome precompilati dal provider), poi prosegue identico.
          navigate(`/register/profile?token=${startData.sessionToken}`, { replace: true });
          return;
        }

        // 2FA come step successivo anche per l'SSO (parità col flusso password
        // di auth.ts signIn: stesso skip in DEV, stesso fail-closed su errore,
        // stesso /2fa-setup se richiesto ma non configurato).
        if (!import.meta.env.DEV) {
          try {
            const { data: twoFaStatus, error: twoFaError } = await supabase.functions.invoke('twofa-get-status', {
              headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (twoFaError) throw twoFaError;

            if (twoFaStatus?.required) {
              if (!twoFaStatus.setupCompleted) {
                navigate('/2fa-setup', { state: { redirectTo: returnPathRef.current } });
                return;
              }
              // Dispositivo ricordato (30 giorni): la sessione e' registrata come verificata, niente codice
              const trustedSessionToken = await tryTrustedDeviceLogin();
              if (trustedSessionToken) {
                setTwoFactorSession(trustedSessionToken, new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString());
                await proceedToApp({ profile, userId, provider });
                return;
              }
              set2faPendingFlag(userId);
              proceedCtxRef.current = { profile, userId, provider };
              setTwoFaStep({
                email: session.user.email || '',
                methods: twoFaStatus.enabledMethods || twoFaStatus.availableMethods || ['totp', 'email'],
              });
              return;
            }
          } catch (twoFaCheckError) {
            console.error('[OAuthCallback] 2FA check failed:', twoFaCheckError);
            await supabase.auth.signOut({ scope: 'local' });
            setError('Impossibile verificare lo stato 2FA. Riprova.');
            setTimeout(() => navigate('/login'), 3000);
            return;
          }
        }

        await proceedToApp({ profile, userId, provider });
      } catch (err) {
        console.error('[OAuthCallback] Unexpected error:', err);
        setError('Errore imprevisto. Riprova.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [navigate, proceedToApp]);

  // Step 2FA post-SSO: verifica codice, poi prosegue verso l'app
  if (twoFaStep) {
    return (
      <SsoTwoFactorStep
        email={twoFaStep.email}
        availableMethods={twoFaStep.methods}
        onVerified={() => {
          const ctx = proceedCtxRef.current;
          if (ctx) proceedToApp(ctx);
          else navigate(returnPathRef.current || '/', { replace: true });
        }}
      />
    );
  }

  if (error) {
    return (
      <OAuthCallbackLayout
        status="error"
        title="Accesso non riuscito"
        description={error}
        footerNote="Verrai reindirizzato al login..."
      />
    );
  }

  return (
    <OAuthCallbackLayout
      status="processing"
      title="Accesso in corso"
      description="Stiamo completando l'accesso al tuo account."
    />
  );
}
